// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Anchor, Group, Loader, Paper, Stack, Table, Text, Title } from '@mantine/core';
import type { DiagnosticReport, Patient } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { IconExternalLink } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { type JSX, useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

function getReportLabel(report: DiagnosticReport): string {
  const date = report.presentedForm?.[0]?.creation ?? report.effectiveDateTime ?? report.effectivePeriod?.start;
  if (!date) {
    return 'HSAT Results';
  }
  return `HSAT Results — ${dayjs(date).format('MMMM D, YYYY')}`;
}

function getStatusLabel(status: DiagnosticReport['status']): string {
  const map: Record<string, string> = {
    final: 'Final',
    amended: 'Amended',
    corrected: 'Corrected',
    appended: 'Appended',
    partial: 'In Review',
    preliminary: 'In Review',
    registered: 'In Review',
    unknown: 'In Review',
    cancelled: 'Cancelled',
    'entered-in-error': 'Error',
  };
  return (status && map[status]) ?? 'In Review';
}

function isReportViewable(status: DiagnosticReport['status']): boolean {
  return status === 'final' || status === 'amended' || status === 'corrected' || status === 'appended';
}

export function DiagnosticReportsPage(): JSX.Element {
  const { serviceRequestId } = useParams<{ serviceRequestId: string }>();
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Patient | undefined;
  const navigate = useNavigate();

  const [reports, setReports] = useState<DiagnosticReport[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!profile?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const params: Record<string, string> = {
      subject: `Patient/${profile.id}`,
      _sort: '-date',
      _count: '50',
    };
    if (serviceRequestId) {
      params['based-on'] = `ServiceRequest/${serviceRequestId}`;
    }
    medplum
      .searchResources('DiagnosticReport', params)
      .then((results) => {
        setReports(Array.from(results) as DiagnosticReport[]);
        setLoading(false);
      })
      .catch(() => {
        setReports([]);
        setLoading(false);
      });
  }, [medplum, profile?.id, serviceRequestId]);

  const openPdf = useCallback(
    async (report: DiagnosticReport) => {
      const rawUrl = report.presentedForm?.[0]?.url;
      if (!rawUrl) {
        return;
      }
      let href: string;
      if (rawUrl.startsWith('Binary/')) {
        const blob = await medplum.download(rawUrl);
        href = URL.createObjectURL(blob);
      } else {
        href = rawUrl;
      }
      window.open(href, '_blank', 'noopener,noreferrer');
    },
    [medplum]
  );

  return (
    <Stack gap="md" p="md">
      <Paper withBorder radius="md" shadow="xs" p="xl">
        <Anchor component="button" type="button" size="xs" fw={700} c="polaris" onClick={() => navigate('/my-chart')}>
          My Chart &gt;
        </Anchor>
        <Title order={1} size="2.5rem" fw={700} mt="xs">
          Diagnostic Reports
        </Title>
      </Paper>

      <Paper withBorder radius="md" shadow="sm" p="xl">
        {loading && (
          <Stack align="center" py="md">
            <Loader size="sm" />
          </Stack>
        )}
        {!loading && reports.length === 0 && (
          <Text c="dimmed" size="sm">
            No diagnostic reports on file for this test.
          </Text>
        )}
        {!loading && reports.length > 0 && (
          <Table highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Report</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Action</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {reports.map((report) => (
                <Table.Tr key={report.id}>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {getReportLabel(report)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{getStatusLabel(report.status)}</Text>
                  </Table.Td>
                  <Table.Td>
                    {isReportViewable(report.status) && report.presentedForm?.[0]?.url ? (
                      <Anchor
                        component="button"
                        type="button"
                        size="xs"
                        c="polaris"
                        fw={600}
                        onClick={() => {
                          openPdf(report).catch(console.error);
                        }}
                      >
                        <Group gap={4} wrap="nowrap">
                          Open PDF
                          <IconExternalLink size={12} />
                        </Group>
                      </Anchor>
                    ) : (
                      !isReportViewable(report.status) && (
                        <Text size="xs" c="dimmed">
                          In Review
                        </Text>
                      )
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>
    </Stack>
  );
}
